import { CheckCircle, Email, Error } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Container,
  Paper,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import LogoLoader from "../components/common/LogoLoader";
import { authService } from "../services/authService";

interface VerificationResponse {
  message: string;
  user?: {
    id: number;
    username: string;
    email: string;
  };
}

interface ResendResponse {
  message: string;
  email_sent: boolean;
}

const VerifyEmail: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendEmail, setResendEmail] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState<string | null>(null);

  const token = searchParams.get("token");

  useEffect(() => {
    if (token) {
      verifyEmailToken(token);
    }
  }, [token]);

  const verifyEmailToken = async (verificationToken: string) => {
    setLoading(true);
    setError(null);

    try {
      const response = await authService.verifyEmail(verificationToken);
      setVerified(true);
    } catch (err: any) {
      setError(
        err.response?.data?.message || err.message || "Verification failed"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!resendEmail.trim()) {
      setResendMessage("Please enter your email address");
      return;
    }

    setResendLoading(true);
    setResendMessage(null);

    try {
      const response = await fetch("/api/auth/resend-verification/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: resendEmail }),
      });

      const data: ResendResponse = await response.json();

      if (response.ok) {
        setResendMessage("Verification email sent! Please check your inbox.");
      } else {
        setResendMessage(data.message || "Failed to send verification email");
      }
    } catch (err) {
      setResendMessage("Network error occurred");
    } finally {
      setResendLoading(false);
    }
  };

  const handleGoToLogin = () => {
    navigate("/login");
  };

  return (
    <Container component="main" maxWidth="md">
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          py: 6,
        }}
      >
        {/* Logo */}
        <Box sx={{ mb: 4, textAlign: "center" }}>
          <img
            src="/aa_logo_highres_white_900.png"
            alt="Alpha Affinity Logo"
            style={{
              height: "120px",
              width: "auto",
              objectFit: "contain",
            }}
          />
        </Box>

        <Paper
          elevation={4}
          sx={{
            p: 5,
            textAlign: "center",
            borderRadius: 3,
            maxWidth: 600,
            width: "100%",
            background: "linear-gradient(135deg, #1e1e1e 0%, #2a2a2a 100%)",
          }}
        >
          {loading ? (
            <Box>
              <LogoLoader size="panel" surface="paper" label="Verifying your email" sx={{ mb: 3 }} />
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
                Verifying your email...
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Please wait while we verify your email address.
              </Typography>
            </Box>
          ) : verified ? (
            <Box>
              <Box
                sx={{
                  backgroundColor: "rgba(46, 125, 50, 0.2)",
                  borderRadius: "50%",
                  width: 100,
                  height: 100,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto",
                  mb: 3,
                }}
              >
                <CheckCircle sx={{ fontSize: 60, color: "success.main" }} />
              </Box>
              <Typography
                variant="h3"
                gutterBottom
                sx={{ fontWeight: 600, mb: 2 }}
              >
                Email Verified!
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                Your email has been successfully verified. You can now sign in
                and access the Market Graph Platform.
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={handleGoToLogin}
                sx={{
                  py: 1.5,
                  px: 4,
                  fontWeight: 600,
                  textTransform: "none",
                  fontSize: "1rem",
                }}
              >
                Go to Login
              </Button>
            </Box>
          ) : error ? (
            <Box>
              <Box
                sx={{
                  backgroundColor: "rgba(211, 47, 47, 0.2)",
                  borderRadius: "50%",
                  width: 100,
                  height: 100,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto",
                  mb: 3,
                }}
              >
                <Error sx={{ fontSize: 60, color: "error.main" }} />
              </Box>
              <Typography
                variant="h4"
                gutterBottom
                sx={{ fontWeight: 600, mb: 2 }}
              >
                Verification Failed
              </Typography>
              <Alert
                severity="error"
                sx={{ mb: 4, textAlign: "left", borderRadius: 2 }}
              >
                {error}
              </Alert>

              {/* Contact support section */}
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  backgroundColor: "rgba(25, 118, 210, 0.1)",
                  border: "1px solid",
                  borderColor: "primary.main",
                  borderRadius: 2,
                  textAlign: "left",
                }}
              >
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Need a new verification link?
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  If your verification link has expired or you didn't receive
                  it, please contact our support team.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Email us at:{" "}
                  <Typography
                    component="a"
                    href="mailto:support@alpha-affinity.com"
                    sx={{
                      color: "primary.main",
                      textDecoration: "none",
                      fontWeight: 600,
                      "&:hover": {
                        textDecoration: "underline",
                      },
                    }}
                  >
                    support@alpha-affinity.com
                  </Typography>
                </Typography>
              </Paper>
            </Box>
          ) : (
            <Box>
              <Box
                sx={{
                  backgroundColor: "rgba(158, 158, 158, 0.2)",
                  borderRadius: "50%",
                  width: 100,
                  height: 100,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto",
                  mb: 3,
                }}
              >
                <Email sx={{ fontSize: 60, color: "text.secondary" }} />
              </Box>
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
                Email Verification
              </Typography>
              <Typography variant="body1" color="text.secondary">
                No verification token provided. Please check your email for the
                verification link.
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default VerifyEmail;
